import { AppContextProps } from '../../hooks/useApp'
import { BatteryStateWidgetConfig, DashboardViewProps, Widget, WidgetData } from './Widget'
import { SystemControl } from '../SystemControl'
import { Box, IconButton, Stack, Typography, useTheme } from '@mui/material'
import { Battery0BarOutlined, Battery1BarOutlined, Battery2BarOutlined, Battery3BarOutlined, Battery4BarOutlined, Battery5BarOutlined, Battery6BarOutlined, Battery80Outlined, Battery90Outlined, BatteryFull, InfoOutlined, Settings } from '@mui/icons-material'
import { BatteryOptimizerPortControl } from '../ports/BatteryOptimizerPortControl'
import { BatteryOptimizer2PortControl } from '../ports/BatteryOptimizer2PortControl'
import { BatteryOptimizerSocPlanPortControl } from '../ports/BatteryOptimizerSocPlanPortControl'
import { EnergyStoragePortControl } from '../ports/EnergyStoragePortControl'
import { OptimizerEvents, OptimizerEvents2 } from '../../types/ports'
import { Button } from '../../components/atoms/Button'
import { ExpanderButton } from '../../components/atoms/ExpanderButton'
import { Trans } from 'react-i18next'
import { lang } from '../../utils/lang'
import { BatterySchedule } from '../../components/BatterySchedule'
import { BatterySchedule2 } from '../../components/BatterySchedule2'
import { Link } from 'react-router-dom'
import { LinkTo } from '../../components/atoms/LinkTo'
import { ManualBatteryControl } from '../../components/ManualBatteryControl'
import { ID } from '../../types/models'

const CMD_CHARGING = -1_000_000
const CMD_DISCHARGING = 1_000_000
const CMD_STOP = 0
const CMD_CANCEL = 2 ** 31 - 1
const CMD_START_COMMISSIONING = 2 ** 31 - 2
const CMD_CANCEL_COMMISSIONING = 2 ** 31 - 3

export class BatteryStateWidget extends Widget<{ }, BatteryStateWidgetConfig> {

  open: boolean
  // Opt-in to the new batopt2 plan view. Defaults to the legacy view; the user
  // flips this with the "New view [BETA]" / "Old view" link. Kept as instance
  // state (re-rendered via props.refresh()) like `open`.
  showBatopt2Beta: boolean

  constructor(data: WidgetData, control: SystemControl, app: AppContextProps) {
    super(data, control, app)
    this.open = false
    this.showBatopt2Beta = false
  }

  get Icon() {
    const soc: number = this.batteryControl().getValue('soc')

    if (soc <= 5) return Battery0BarOutlined
    if (soc <= 15) return Battery1BarOutlined
    if (soc <= 25) return Battery2BarOutlined
    if (soc <= 35) return Battery3BarOutlined
    if (soc <= 45) return Battery4BarOutlined
    if (soc <= 55) return Battery5BarOutlined
    if (soc <= 65) return Battery6BarOutlined
    if (soc <= 85) return Battery80Outlined
    if (soc <= 95) return Battery90Outlined
    return BatteryFull
  }

  get width(): number {
    return 4
  }

  get height(): number {
    return 2
  }

  get textColor(): string {
    return 'statsText.main'
  }

  get bgColor(): string {
    return 'statsBackground.main'
  }

  batteryControl(): EnergyStoragePortControl {
    return this.findOneControl('ENERGY_STORAGE') as EnergyStoragePortControl
  }

  optimizerControl(): BatteryOptimizerPortControl {
    return this.findOneControl('BATTERY_OPTIMIZER') as BatteryOptimizerPortControl
  }

  /**
   * Return the batopt2 port control if the appliance has one, otherwise null.
   * Unlike `optimizerControl`, this does not throw when missing — presence is
   * what decides whether we render the legacy or the new plan view.
   */
  optimizer2Control(): BatteryOptimizer2PortControl | null {
    const match = this.findControslByFilter(c => c.port.type === 'BATTERY_OPTIMIZER2')
    return (match[0] as BatteryOptimizer2PortControl) ?? null
  }

  socPlanControl(): BatteryOptimizerSocPlanPortControl | null {
    const match = this.findControslByFilter(c => c.port.type === 'BATTERY_OPTIMIZER_SOC_PLAN')
    return (match[0] as BatteryOptimizerSocPlanPortControl) ?? null
  }

  /**
   * Whether the battery is in maintenance mode, i.e. the firmware's battery
   * optimizer is off and the battery only moves on direct commands.
   */
  isManualControl(): boolean {
    // Read the appliance config rather than asking BatteryAppliance: importing
    // it here would close the cycle BatteryAppliance -> widgets -> this file
    // and leave the subclasses extending `undefined`.
    return this.appliances[0]?.getConfig('strategy') === 'BATTERY_MANUAL'
  }

  /**
   * The setpoint the device reports having received, under the live power.
   *
   * Shown in both modes: in maintenance mode it confirms the manual command
   * actually reached the inverter, and while optimizing it shows what the
   * firmware is asking of the battery right now. `received_command` is null
   * when the device has no standing command, in which case nothing is drawn.
   */
  sentCommand(): React.ReactElement | null {
    const command: number | null | undefined = this.batteryControl().getValue('received_command')

    if (command === null || command === undefined || isNaN(command)) {
      return null
    }

    // Same sign convention as `power`: positive discharges, negative charges.
    const label = !command
      ? this.app.t('COMMAND_STOP')
      : (command > 0 ? this.app.t('COMMAND_DISCHARGE') : this.app.t('COMMAND_CHARGE'))

    return <Box data-cy="box-Sent command" sx={{ ml: 2, mt: 0.5, fontSize: '85%', color: 'text.secondary' }}>
      ({this.app.t('Sent command')}: {label} {lang.num(Math.abs(command) / 1000, 3)}kW)
    </Box>
  }

  draw(props: DashboardViewProps): React.ReactElement {

    const Icon = this.Icon
    const optimizer = this.optimizerControl()
    const optimizer2 = this.optimizer2Control()
    const socPlan = this.socPlanControl()
    const battery = this.batteryControl()
    const events: OptimizerEvents = optimizer.getValue('events') || []
    const events2: OptimizerEvents2 = (optimizer2?.getValue('events') as OptimizerEvents2) || []
    const socPct: number[] = (socPlan?.getValue('soc_pct') as number[]) || []
    // A batopt2 control with events only exists for new (15-min) firmware —
    // the server drops old-frame port 222 messages, so old units never get
    // here. The new view is opt-in via the BETA link; default stays legacy.
    const hasBatopt2 = events2.length > 0
    const useBatopt2 = hasBatopt2 && this.showBatopt2Beta
    const errorFlags = this.errorFlags()
    const hasErrors = errorFlags.length > 0 || battery.getValue('status') !== 'SPP_ENERGYSTORAGE_INFO_STATUS_OK'
    const soc = battery.getValue('soc')
    const isOffline = soc === undefined
    const socText = isOffline || soc === null || isNaN(Math.round(soc as number)) ? '—' : `${Math.round(soc as number)}%`

    const onForce = (power: number): void => {
      optimizer.sendCommand({ name: 'override_power', value: power })
    }

    const error: number = optimizer.getValue('commissioning_errcode') || 0
    const state: number = optimizer.getValue('commissioning_state') || 0
    const power: number = battery.getValue('power') || 0
    const chargingState = !power
      ? this.app.t('State-IDLE')
      : (
          power > 0 ? this.app.t('State-DISCHARGING') : this.app.t('State-CHARGING')
        )

    const overriding = events.length > 0 && events[0][1].state === 'BATTERY_OPTIMIZER_STATE_OVERRIDE'
    const isFcr = this.appliance.type === 'FCR_BATTERY'
    // Maintenance mode: the firmware's battery optimizer is off, so there is
    // no plan to draw and the override / commissioning buttons (which the
    // optimizer would have to act on) would do nothing.
    const manual = this.isManualControl()
    const commissioning = state > 0 && state < 100
    const commissioningInitialized = optimizer.getValue('commissioning_state') !== undefined

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const theme = useTheme()

    return (
      <Box sx={{
        backgroundColor: this.bgColor,
        boxShadow: theme.boxShadow.main,
        borderRadius: '0.4rem',
        height: '100%',
      }} data-cy={`box-${this.getName()}`}>
        <Stack>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: '2.5rem auto 5rem 2rem',
            alignItems: 'center',
            paddingLeft: '1rem',
            height: '2rem',
          }}>
            <Icon sx={{ color: isOffline ? 'gray' : theme.palette.iconColor.main }}/>
            <Typography sx={{ color: isOffline ? 'gray' : this.textColor }}>{this.getName()}</Typography>
            <Box sx={{ color: props.upgrading ? 'gray' : this.textColor, pr: 2, textAlign: 'right', fontWeight: 'bold', fontSize: '130%' }}>{socText}</Box>
            <ExpanderButton open={this.open} disabled={props.upgrading} color={this.textColor} onToggle={() => {
              this.open = !this.open
              props.refresh()
            }}/>
          </Box>

          <Box sx={{ color: 'error.light', pl: 7, pb: 2, m: 0, fontSize: '85%' }}>
            { props.upgrading && <Box sx={{ color: 'primary.light' }}><Trans>Upgrading...</Trans></Box>}
            { !props.upgrading && errorFlags.join(' ')}&nbsp;
          </Box>

          {this.open && !props.upgrading &&
            <Box sx={{ mx: 2, mt: 1, mb: 2 }}>
              {manual
                ? <Box sx={{ mx: 1, my: 1 }}>
                    <ManualBatteryControl control={battery} applianceId={this.appliances[0].id as ID} />
                  </Box>
                : <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr' }}>
                  {!overriding && <Button disabled={hasErrors || commissioning} sx={{ m: 1, p: 0, fontSize: '90%' }} onClick={() => onForce(CMD_CHARGING)} size="small" variant="text">
                    <Trans>Charge 1h</Trans>
                  </Button>}
                  {!overriding && <Button disabled={hasErrors || commissioning} sx={{ m: 1, p: 0, fontSize: '90%' }} onClick={() => onForce(CMD_DISCHARGING)} size="small" variant="text">
                    <Trans>Discharge 1h</Trans>
                  </Button>}
                  {!overriding && <Button disabled={hasErrors || commissioning} sx={{ m: 1, p: 0, fontSize: '90%' }} onClick={() => onForce(CMD_STOP)} size="small" variant="text">
                    <Trans>Stop 1h</Trans>
                  </Button>}

                  {overriding && <Button disabled={hasErrors} sx={{ m: 1, p: 0, fontSize: '90%' }} onClick={() => onForce(CMD_CANCEL)} size="small" variant="text">
                    <Trans>Cancel Override</Trans>
                  </Button>}
                </Box>}

                <Box sx={{ ml: 2, mt: 2, fontWeight: 'bold', fontSize: '1.2rem' }}>
                  {chargingState} {lang.num(Math.abs(power) / 1000)}kW
                </Box>

                {this.sentCommand()}

                <Box sx={{ ml: 2, mt: 2, display: isFcr && !manual ? 'block' : 'none' }}>
                {
                  commissioningInitialized && state === 0 && error === 0 && <Box>
                    <Typography sx={{ color: 'orange' }}>
                      {this.app.t(`Commissioning-State-${state}`)}
                    </Typography>
                    <Button disabled={hasErrors} sx={{ mt: 2, px: 3, fontSize: '90%' }} onClick={() => onForce(CMD_START_COMMISSIONING)} size="small" variant="text">
                      <Trans>Start Commissioning</Trans>
                    </Button>
                  </Box>
                }
                {
                  commissioningInitialized && state !== 0 && error === 0 && <Box>
                    {state === 100
                      ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{
                            width: 7,
                            height: 7,
                            borderRadius: '50%',
                            bgcolor: 'success.light',
                            boxShadow: '0 0 8px rgba(9, 231, 164, 0.55)',
                            flexShrink: 0
                          }}/>
                          <Typography sx={{ color: 'grey.500', fontSize: '90%' }}>
                            {this.app.t(`Commissioning-State-${state}`)}
                          </Typography>
                        </Box>
                      : <Typography sx={{ color: 'success.light' }}>
                          {this.app.t(`Commissioning-State-${state}`)}
                        </Typography>}
                    {
                      state !== 100 && <Button disabled={hasErrors} sx={{ mt: 2, px: 3, fontSize: '90%' }} onClick={() => onForce(CMD_CANCEL_COMMISSIONING)} size="small" variant="text">
                        <Trans>Cancel Commissioning</Trans>
                      </Button>
                    }
                  </Box>
                }
                {
                  commissioningInitialized && error !== 0 && <Box>
                    <Typography sx={{ color: 'error.light' }}>
                      {this.app.t(`Commissioning-Error-${error}`)}
                    </Typography>
                    <Button disabled={hasErrors} sx={{ mt: 2, px: 3, fontSize: '90%' }} onClick={() => onForce(CMD_START_COMMISSIONING)} size="small" variant="text">
                      <Trans>Start Commissioning</Trans>
                    </Button>
                  </Box>
                }
                </Box>

                <Box sx={{ ml: 2, mt: 3 }}>
                  {!manual && <>
                  <Trans>Current plan</Trans>:
                  {useBatopt2
                    ? <BatterySchedule2
                        sx={{ mt: 1 }}
                        events={events2}
                        socPct={socPct}
                        currentSoc={typeof soc === 'number' ? soc : null}
                        portId={optimizer2 ? (optimizer2.port.id as number) : null}
                      />
                    : <BatterySchedule sx={{ mt: 1, color: theme.palette.primary.main }} events={events}/>
                  }
                  {hasBatopt2 &&
                    <Box sx={{ mt: 1 }}>
                      <Typography
                        component="span"
                        onClick={() => {
                          this.showBatopt2Beta = !this.showBatopt2Beta
                          props.refresh()
                        }}
                        sx={{
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          fontSize: '90%',
                          color: theme.palette.primary.main,
                        }}
                      >
                        {useBatopt2 ? <Trans>Old view</Trans> : <Trans>New view [BETA]</Trans>}
                      </Typography>
                    </Box>
                  }
                  </>}
                  <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 0.75, color: 'grey.400' }}>
                    <InfoOutlined sx={{ fontSize: '0.95rem', opacity: 0.7, flexShrink: 0 }} />
                    <LinkTo
                      sx={{
                        color: 'grey.400',
                        fontSize: '85%',
                        textDecoration: 'underline',
                        textDecorationColor: 'rgba(163, 163, 163, 0.4)',
                        textUnderlineOffset: '3px',
                        '&:hover': {
                          color: 'grey.100',
                          textDecorationColor: '#F0F0F0'
                        }
                      }}
                      external
                      title={this.app.t('More information about battery optimization and reserve markets.')}
                      url="https://www.enion.fi/s/Akkualgoritmi_ja_reservitietopaketti.pdf"
                    />
                  </Box>
                </Box>

                {this.app.installerLevel && <Link to={`/appliances/${this.appliances[0].id}`}>
                  <Stack direction="row-reverse" sx={{ mt: 2, cursor: 'pointer' }}>
                    <IconButton sx={{ transform: 'translateY(-25%)' }}>
                      <Settings />
                    </IconButton>
                    <Typography sx={{ textDecoration: 'underline' }}>
                      <Trans>Settings</Trans>
                    </Typography>
                  </Stack>
                </Link>}

            </Box>
          }
        </Stack>
      </Box>
    )
  }
}
